import { XYZ_DOCUMENTED_INSTRUMENTS } from "./xyz-markets";

/** Displayed instrument ticker → xyz coin (e.g. "WTIOIL" → "CL", "SKHYNIX" → "SKHX").
 *  Display symbols use the instrument name, but trade routes use the raw coin. */
const XYZ_COIN_BY_INSTRUMENT: Record<string, string> = Object.fromEntries(
	Object.entries(XYZ_DOCUMENTED_INSTRUMENTS).map(([coin, instrument]) => [
		instrument,
		coin,
	]),
);

// Mirrors createTradeRouteSymbol (hyperliquid.ts) — not imported so markdown
// consumers (learn articles) don't pull the Hyperliquid SDK into their chunk.
function toRouteSymbol(displayBase: string): string {
	const xyzCoin = XYZ_COIN_BY_INSTRUMENT[displayBase];
	return xyzCoin ? `xyz-${xyzCoin}` : displayBase;
}

/**
 * Turn plain-text market symbols ("WTIOIL-PERP") in markdown into
 * `[WTIOIL-PERP](/trade/xyz-CL)` links. Runs at render time so the i18n
 * source bodies stay link-free; undocumented tickers fall through to the
 * main-dex route shape. Idempotent — already-linked symbols are skipped.
 *
 * A `**SYM-PERP**` wrapper is hoisted INSIDE the link (`[**SYM**](url)`):
 * leaving it outside yields `)**` + a CJK particle, which CommonMark never
 * closes (the ko "**심볼**로" pattern) and the asterisks render literally.
 */
export function linkifyTradeSymbols(markdown: string): string {
	// Trailing guard excludes `*` too: without it, backtracking can drop the
	// close-** capture on `[**SYM**](…)` and re-link the inner symbol.
	return markdown.replace(
		/(?<!\[)(\*\*)?\b([A-Z][A-Z0-9]{1,14})-PERP\b(\*\*)?(?![\]([*])/g,
		(match, open: string | undefined, base: string, close: string | undefined) => {
			// Unpaired ** means the symbol sits inside a longer bold phrase —
			// rewriting would corrupt the emphasis, so leave it untouched.
			if (Boolean(open) !== Boolean(close)) return match;
			const label = open ? `**${base}-PERP**` : `${base}-PERP`;
			return `[${label}](/trade/${toRouteSymbol(base)})`;
		},
	);
}
